/**
 * ActrContext.tsx - ActrContext - React Context for Actr
 * 
 * CHANGE HISTORY:
 * - 251105_0945 danielbeer: Add TypeScript types to fix useActr hook
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { createContext, useContext, useMemo } from 'react';
import { Actr } from './Actr';
import type { ActrOptions } from './types/ActrTypes';

interface ActrProviderProps {
  children: React.ReactNode;
  options?: ActrOptions;
  actr?: Actr | null;
}

const ActrContext = createContext<Actr | null>(null);

/**
 * ActrProvider - Provides Actr instance to children
 */
export function ActrProvider({ children, options = {}, actr = null }: ActrProviderProps) {
  const actrInstance = useMemo(() => {
    return actr || new Actr(options);
  }, [actr, options]);

  return (
    <ActrContext.Provider value={actrInstance}>
      {children}
    </ActrContext.Provider>
  );
}

/**
 * useActr - Hook to access Actr instance
 */
export function useActr(): Actr {
  const actr = useContext(ActrContext);
  
  if (!actr) {
    throw new Error('useActr must be used within ActrProvider');
  }
  
  return actr;
}

/**
 * useAction - Hook to execute an action
 */
export function useAction(actionName: string) {
  const actr = useActr();
  
  return async (params: any) => {
    return await actr.execute(actionName, params);
  };
}

export default ActrContext;
